// external
import type { Reducer } from 'redux';
import type { Draft } from 'immer';
import produce from 'immer';

// libraries
import { initActionTypes } from '@makemydeal/dr-shared-store';
import { CASH } from '@makemydeal/dr-platform-types';
import { actions as offerActions } from '@makemydeal/dr-offer-redux';
import type { BaggageEntry } from '@makemydeal/dr-dash-bff-types';

// utils
import {
    getRequestedSurcharges,
    getModifiedProducts,
    mapToDealerProducts,
    mapToInitialDealXgProducts,
    mapToPaymentProductCodes,
    selectAllEligibleProducts,
    syncVppProducts,
    unselectAllEligibleProducts,
    updateProductsWithRates,
    sortProducts,
    toDarwinBaseLineProducts
} from '../utils/vehicleProtectionUtils';

// types
import { MAX_FETCH_RATES_POLLING_COUNT, RATE_REQUEST_STATUSES } from '../types/vehicleProtectionTypes';
import { PAYMENT_FAILURE, PAYMENT_RECEIVED, PAYMENT_REQUESTED } from '../actionTypes/deskingActionTypes';
import type { DraftDealProduct, VehicleProtectionState } from '@makemydeal/dr-dash-types';
import { RefreshContractValidationState } from '@makemydeal/dr-dash-types';
import {
    ALL_PRODUCTS_MONTHLY_PAYMENT_RESET,
    ALL_PRODUCTS_SELECTED,
    ALL_PRODUCTS_UNSELECTED,
    FETCH_PRODUCTS_FAILURE,
    FETCH_PRODUCTS_REQUESTED,
    FETCH_PRODUCTS_RETRY,
    FETCH_PRODUCTS_SUCCESS,
    FETCH_RATES_FAILURE,
    FETCH_RATES_REQUESTED,
    FETCH_RATES_RETRY,
    FETCH_RATES_SUCCESS,
    PRODUCT_CHANGES_PAYMENT_CALL,
    GAP_PRICE_UPDATED_BY_LENDER,
    GAP_PRICE_ACKNOWLEDGED,
    PRODUCT_MONTHLY_PAYMENT_UPDATED,
    PRODUCT_TAXED_UPDATED,
    PRODUCT_SELECTED,
    PRODUCT_UNSELECTED,
    PRODUCT_UPDATED,
    PRODUCTS_REQUEST_OVERRIDDEN,
    SET_MANUAL_PRODUCTS,
    SET_EDITABLE_PRODUCT_CODE,
    SET_REFRESH_CONTRACT_VALIDATION_STATE
} from '../actionTypes/vehicleProtectionActionTypes';
import { APPLY_ORIGINAL_TEMPLATE_PRODUCTS, APPLY_TEMPLATE_PRODUCTS } from '../actionTypes/dealTemplateActionTypes';
import type { ManualProduct, DarwinBaselineProducts } from '@makemydeal/dr-shared-types';
import { MV_VPP_OVERRIDES_BAGGAGE_NAME } from '../constants';

const isDealWithAppliedTemplate = (
    dealVehicleProtection: { products: DraftDealProduct[] },
    template?: { products?: unknown[] }
): boolean => dealVehicleProtection.products.length === 0 && !!template?.products?.length;

const getDarwinBaselineProducts = (baggage?: BaggageEntry[]): DarwinBaselineProducts | undefined => {
    if (!Array.isArray(baggage)) {
        return undefined;
    }

    return baggage.find(({ name }: BaggageEntry) => name === MV_VPP_OVERRIDES_BAGGAGE_NAME)?.value?.content;
};

const { INIT_PENCIL_SUCCESS } = initActionTypes;

export const initialState: VehicleProtectionState = {
    products: [],
    dealXgProducts: [],
    dealerProducts: [],
    modifiedProducts: [],
    rates: [],
    rateToken: '',
    surcharges: {},
    requestedSurcharges: {
        usedSurchargesAttributes: [],
        requiredSurchargesAttributes: []
    },
    isFetchingProducts: false,
    isFetchingRates: false,
    lastFailureAction: null,
    isPaymentFailed: false,
    isRatingFailed: false,
    isRatingNetworkFailure: false,
    isRatingTimeout: false,
    isFetchRatesRetried: false,
    isGapCappedByLender: false,
    paymentProductCodes: [],
    wasGapSelectedBeforeCash: undefined,
    isGapRemovedForCash: false,
    lastSuccessfulPaymentProductCodes: [],
    targetProducts: [],
    isTemplateProductsApplied: false,
    editableProductCode: undefined,
    refreshContractValidationState: RefreshContractValidationState.IDLE,
    darwinBaselineProducts: {}
};

export type VehicleProtectionReducer = Reducer<VehicleProtectionState>;

export const reducer: VehicleProtectionReducer = (state = initialState, action) => {
    if (!action) {
        return state;
    }

    return produce(state, (draft: Draft<VehicleProtectionState>) => {
        switch (action.type) {
            case INIT_PENCIL_SUCCESS: {
                const { dealVehicleProtection, template } = action.payload;
                let dealProducts: DraftDealProduct[] = [];
                if (dealVehicleProtection?.products) {
                    dealProducts = dealVehicleProtection.products as DraftDealProduct[];

                    draft.products = sortProducts(dealProducts);
                    draft.dealXgProducts = mapToInitialDealXgProducts(dealProducts);

                    if (isDealWithAppliedTemplate(dealVehicleProtection, template)) {
                        draft.modifiedProducts = [];
                    }

                    draft.modifiedProducts = getModifiedProducts(draft, dealProducts);
                    draft.lastSuccessfulPaymentProductCodes = mapToPaymentProductCodes(dealProducts);
                    draft.isFetchingRates = true;
                    draft.targetProducts = template?.products ? template.products : [];
                    draft.isTemplateProductsApplied = false;
                }
                if (dealVehicleProtection?.surchargesOverrides) {
                    draft.surcharges = dealVehicleProtection.surchargesOverrides;
                }

                const dealXgDerivedBaseline =
                    dealProducts.length > 0 ? toDarwinBaseLineProducts(dealProducts.filter((p) => p.selected)) : {};
                const baggageDarwinProducts = getDarwinBaselineProducts(action.payload.baggage);
                draft.darwinBaselineProducts = Object.entries(baggageDarwinProducts ?? {}).reduce<DarwinBaselineProducts>(
                    (merged, [productCode, overrideFields]) => {
                        merged[productCode] = { ...merged[productCode], ...overrideFields };
                        return merged;
                    },
                    { ...dealXgDerivedBaseline }
                );

                break;
            }
            case PRODUCT_SELECTED: {
                const { productCode } = action.payload;

                const selectedProduct = draft.products.find(
                    (product) => product.productCode && product.productCode === productCode
                );

                if (selectedProduct) {
                    selectedProduct.selected = true;
                }

                draft.modifiedProducts = getModifiedProducts(draft, [{ productCode }]);

                break;
            }
            case SET_MANUAL_PRODUCTS: {
                const { products } = action.payload;
                const newManualProducts = products.map((product: ManualProduct) => ({
                    ...product,
                    isManualProduct: true,
                    productProviderName: null,
                    selected: true
                }));
                draft.products = draft.products.filter((p) => !p.isManualProduct).concat(newManualProducts);

                break;
            }
            case PRODUCT_UNSELECTED: {
                const { productCode } = action.payload;
                const selectedProduct = draft.products.find(
                    (product) => product.productCode && product.productCode === productCode
                );

                if (selectedProduct) {
                    selectedProduct.selected = false;
                }

                break;
            }
            case ALL_PRODUCTS_SELECTED: {
                draft.products = selectAllEligibleProducts(draft);
                draft.modifiedProducts = getModifiedProducts(draft, draft.products);

                break;
            }
            case ALL_PRODUCTS_UNSELECTED: {
                draft.products = unselectAllEligibleProducts(draft);

                break;
            }
            case PRODUCT_UPDATED: {
                const { productCode } = action.payload;
                const productToUpdate = draft.products.find(
                    (product) => product.productCode && product.productCode === productCode
                );

                if (productToUpdate) {
                    Object.assign(productToUpdate, action.payload);
                    draft.modifiedProducts = getModifiedProducts(draft, [{ productCode }]);
                }

                if (!action.meta?.skipPayment) {
                    draft.lastSuccessfulPaymentProductCodes = draft.lastSuccessfulPaymentProductCodes.filter(
                        (code) => code !== productCode
                    );
                }
                break;
            }
            case PRODUCTS_REQUEST_OVERRIDDEN: {
                draft.surcharges = action.payload;

                break;
            }
            case FETCH_PRODUCTS_REQUESTED: {
                draft.isFetchingProducts = true;
                draft.isFetchingRates = true;
                draft.isRatingFailed = false;
                draft.isRatingNetworkFailure = false;
                draft.isRatingTimeout = false;
                draft.rates = [];
                break;
            }
            case SET_REFRESH_CONTRACT_VALIDATION_STATE: {
                draft.refreshContractValidationState = action.payload;
                break;
            }
            case FETCH_PRODUCTS_SUCCESS: {
                const { products: dealerProducts = [], surcharges, rateToken } = action.payload;

                draft.isFetchingProducts = false;
                const syncedProducts = syncVppProducts(draft, dealerProducts);
                draft.products = sortProducts(syncedProducts);
                draft.requestedSurcharges = getRequestedSurcharges(surcharges);
                draft.dealerProducts = mapToDealerProducts(dealerProducts);
                draft.rateToken = rateToken;

                break;
            }
            case FETCH_PRODUCTS_FAILURE: {
                draft.isFetchingProducts = false;
                draft.isFetchingRates = false;
                draft.rates = [];

                break;
            }
            case FETCH_PRODUCTS_RETRY: {
                draft.isFetchRatesRetried = false;

                break;
            }
            case FETCH_RATES_REQUESTED: {
                draft.isFetchingRates = true;
                draft.isRatingFailed = false;
                draft.isRatingNetworkFailure = false;
                draft.isRatingTimeout = false;
                draft.rates = [];

                break;
            }
            case FETCH_RATES_SUCCESS: {
                const { status, products, errors = [] } = action.payload;
                const { retryNumber } = action.meta;

                if (status === RATE_REQUEST_STATUSES.FINISHED) {
                    draft.isFetchingRates = false;
                    draft.isRatingFailed = errors.length > 0;
                    draft.isRatingNetworkFailure = false;
                    draft.rates = products;

                    draft.products = updateProductsWithRates(draft);
                } else if (retryNumber > MAX_FETCH_RATES_POLLING_COUNT) {
                    draft.isFetchingRates = false;
                    draft.isRatingFailed = true;
                    draft.isRatingTimeout = true;
                    draft.rates = [];
                }

                break;
            }
            case FETCH_RATES_FAILURE: {
                draft.isFetchingRates = false;
                draft.isRatingFailed = true;
                draft.isRatingNetworkFailure = true;
                draft.rates = [];

                break;
            }
            case FETCH_RATES_RETRY: {
                draft.isFetchRatesRetried = true;

                break;
            }
            case PAYMENT_REQUESTED: {
                draft.lastFailureAction = null;
                draft.isPaymentFailed = false;
                draft.paymentProductCodes = mapToPaymentProductCodes(draft.products);

                break;
            }
            case PAYMENT_RECEIVED: {
                if (!action.meta?.originalAction) break;
                if (!action.meta.offerType) break;
                if (
                    action.payload[action.meta.offerType]?.success !== true &&
                    action.meta.originalAction.type === PRODUCT_CHANGES_PAYMENT_CALL
                ) {
                    draft.lastFailureAction = action.meta.originalAction.type;
                    draft.isPaymentFailed = true;
                }

                const requestProductCodes = action.meta.originalAction.meta?.middleware?.productCodes;
                if (requestProductCodes) {
                    draft.lastSuccessfulPaymentProductCodes = requestProductCodes;
                } else {
                    draft.lastSuccessfulPaymentProductCodes = draft.paymentProductCodes;
                }

                break;
            }
            case PAYMENT_FAILURE: {
                if (!action.meta?.originalAction) break;
                if (action.meta.originalAction.type === PRODUCT_CHANGES_PAYMENT_CALL) {
                    draft.lastFailureAction = action.meta.originalAction.type;
                    draft.isPaymentFailed = true;
                }

                break;
            }
            case PRODUCT_MONTHLY_PAYMENT_UPDATED: {
                const { productCode, productMonthlyPayment } = action.payload;

                const productToUpdate = draft.products.find(
                    (product) => product.productCode && product.productCode === productCode
                );

                if (productToUpdate && productMonthlyPayment) {
                    productToUpdate.productMonthlyPayment = productMonthlyPayment;
                    // NOTE: monthly price exists only when product price is capitalized
                    // Preserve existing user selection; default to true only if not yet set
                    productToUpdate.isProductPriceCapitalized = productToUpdate.isProductPriceCapitalized ?? true;
                }

                break;
            }
            case PRODUCT_TAXED_UPDATED: {
                const { productCode, isTaxed } = action.payload;

                const productToUpdate = draft.products.find(
                    (product) => product.productCode && product.productCode === productCode
                );

                // NOTE: guard on the product only - `false` is a real value here and must not be dropped.
                if (productToUpdate) {
                    productToUpdate.isTaxed = isTaxed;
                }

                break;
            }
            case GAP_PRICE_UPDATED_BY_LENDER: {
                const { productCode, productPrice } = action.payload;

                const productToUpdate = draft.products.find(
                    (product) => product.productCode && product.productCode === productCode
                );

                if (!productToUpdate) break;

                draft.isGapCappedByLender = true;
                productToUpdate.productPrice = productPrice;
                if (productToUpdate.productCost && productToUpdate.productMarkup) {
                    productToUpdate.productMarkup.markupDollarAmount = productPrice - productToUpdate.productCost;
                }

                break;
            }
            case GAP_PRICE_ACKNOWLEDGED: {
                draft.isGapCappedByLender = false;
                break;
            }
            case ALL_PRODUCTS_MONTHLY_PAYMENT_RESET: {
                draft.products.forEach((product) => delete product.productMonthlyPayment);

                break;
            }
            case APPLY_TEMPLATE_PRODUCTS: {
                draft.isTemplateProductsApplied = true;

                break;
            }
            case APPLY_ORIGINAL_TEMPLATE_PRODUCTS: {
                draft.targetProducts.forEach((product) => {
                    const existingProduct = draft.products.find((p) => p.productCode === product.productCode);

                    if (!existingProduct) {
                        draft.products.push({
                            ...product,
                            selected: true
                        });
                    } else {
                        Object.assign(existingProduct, {
                            ...product,
                            selected: true
                        });
                    }
                });
                draft.modifiedProducts = getModifiedProducts(draft, draft.targetProducts);
                draft.isTemplateProductsApplied = true;

                break;
            }
            case SET_EDITABLE_PRODUCT_CODE: {
                const { productCategoryCode } = action.payload;
                if (productCategoryCode) {
                    draft.editableProductCode = draft.products.find(
                        (product) =>
                            product.productCategoryCode === productCategoryCode || product.productCode === productCategoryCode
                    )?.productCode;
                } else {
                    draft.editableProductCode = undefined;
                }

                break;
            }
            case offerActions.UPDATED_OFFER_TYPE: {
                const newOfferType = action.payload;
                const gapProduct = draft.products.find(
                    (product) => product.productCategoryCode === 'GAP' && !product.isManualProduct
                );

                if (newOfferType === CASH) {
                    draft.wasGapSelectedBeforeCash = gapProduct?.selected ?? false;
                    if (gapProduct?.selected) {
                        gapProduct.selected = false;
                        draft.isGapRemovedForCash = true;
                    }
                } else if (draft.wasGapSelectedBeforeCash !== undefined) {
                    if (draft.wasGapSelectedBeforeCash && gapProduct?.productCode) {
                        gapProduct.selected = true;
                        draft.modifiedProducts = getModifiedProducts(draft, [{ productCode: gapProduct.productCode }]);
                    }
                    draft.wasGapSelectedBeforeCash = undefined;
                    draft.isGapRemovedForCash = false;
                }
                break;
            }
        }
    });
};
